import { useState, useEffect, useCallback } from 'react';

/**
 * Centralized dark mode management utility
 * Manages theme state, localStorage persistence, and system preference detection
 */
export const useDarkMode = () => {
    const [isDarkMode, setIsDarkMode] = useState(false);
    const [isThemeInitialized, setIsThemeInitialized] = useState(false);
    const [userHasSetTheme, setUserHasSetTheme] = useState(false);
    const [isClient, setIsClient] = useState(false);

    // Get theme from localStorage
    const getStoredTheme = useCallback(() => {
        if (typeof window === 'undefined') return null;
        return localStorage.getItem('theme');
    }, []);

    // Set theme in localStorage
    const setStoredTheme = useCallback((theme) => {
        if (typeof window === 'undefined') return;
        localStorage.setItem('theme', theme);
    }, []);

    // Check system preference
    const checkSystemTheme = useCallback(() => {
        if (typeof window === 'undefined') return false;
        return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    }, []);

    // Set dark mode state
    const setDarkMode = useCallback((dark, isUserAction = false) => {
        setIsDarkMode(dark);
        if (isUserAction) {
            setUserHasSetTheme(true);
        }
        setStoredTheme(dark ? 'dark' : 'light');
        
        // Update document attribute for CSS custom properties
        if (typeof document !== 'undefined') {
            document.documentElement.setAttribute('data-theme', dark ? 'dark' : 'light');
        }
    }, []);

    // Toggle dark mode
    const toggleDarkMode = useCallback(() => {
        setDarkMode(!isDarkMode, true);
    }, [isDarkMode, setDarkMode]);

    // Reset to system theme
    const resetToSystemTheme = useCallback(() => {
        setUserHasSetTheme(false);
        const systemTheme = checkSystemTheme();
        setDarkMode(systemTheme);
        localStorage.removeItem('theme'); // Remove saved preference
    }, [checkSystemTheme, setDarkMode]);

    // Set client-side flag
    useEffect(() => {
        setIsClient(true);
    }, []);

    // Initialize theme detection
    useEffect(() => {
        if (!isClient) return;

        const initializeTheme = () => {
            // Check localStorage first
            const savedTheme = getStoredTheme();
            if (savedTheme === 'dark') {
                setDarkMode(true);
                setUserHasSetTheme(true);
            } else if (savedTheme === 'light') {
                setDarkMode(false);
                setUserHasSetTheme(true);
            } else {
                // Fall back to system preference
                setDarkMode(checkSystemTheme());
                setUserHasSetTheme(false);
            }

            // Listen for system theme changes
            const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
            const handleSystemThemeChange = (e) => {
                // Update theme if user hasn't manually set a preference
                if (!userHasSetTheme) {
                    setDarkMode(e.matches);
                }
            };

            mediaQuery.addEventListener('change', handleSystemThemeChange);

            // Listen for theme changes from other components
            const observer = new MutationObserver((mutations) => {
                mutations.forEach((mutation) => {
                    if (mutation.type === 'attributes' && mutation.attributeName === 'data-theme') {
                        const theme = document.documentElement.getAttribute('data-theme');
                        setIsDarkMode(theme === 'dark');
                    }
                });
            });

            observer.observe(document.documentElement, {
                attributes: true,
                attributeFilter: ['data-theme'],
            });

            setIsThemeInitialized(true);

            return () => {
                mediaQuery.removeEventListener('change', handleSystemThemeChange);
                observer.disconnect();
            };
        };

        // Initialize immediately without delay
        initializeTheme();
    }, [isClient, getStoredTheme, setDarkMode, checkSystemTheme, userHasSetTheme]);

    return {
        isDarkMode: isClient ? isDarkMode : false,
        isThemeInitialized: isClient ? isThemeInitialized : false,
        setDarkMode,
        toggleDarkMode,
        resetToSystemTheme,
        getStoredTheme,
        setStoredTheme,
        checkSystemTheme,
        isClient,
    };
};

/**
 * Legacy hook for backward compatibility
 * @deprecated Use useDarkMode instead
 */
export const useTheme = useDarkMode;
